// packages/web/src/context/SiteConfigContext.tsx
import React, { createContext, useContext, useState, useEffect } from 'react'
import { apiGet, apiPut } from '../lib/api'

export interface SiteConfig {
  brand_name: string
  brand_tagline: string
  logo_url: string
  favicon_url: string
  announcement_ticker: string
  guarantee_badge_text: string

  // Hero Banner Dynamic Text & Images
  hero_title_line1: string
  hero_title_line2: string
  hero_subtext: string
  hero_cta_primary: string
  hero_cta_secondary: string
  hero_stat1_value: string
  hero_stat1_label: string
  hero_stat2_value: string
  hero_stat2_label: string
  hero_stat3_value: string
  hero_stat3_label: string
  hero_showcase_badge: string
  hero_showcase_title: string
  hero_showcase_price: string
  hero_showcase_image: string

  // Homepage Section Headings
  category_section_title: string
  category_section_subtitle: string
  catalogue_section_title: string
  catalogue_section_subtitle: string

  // VIP Newsletter & Store Value Badges
  vip_badge_text: string
  vip_title: string
  vip_subtext: string
  vip_button_text: string

  prop1_title: string
  prop1_subtext: string
  prop2_title: string
  prop2_subtext: string
  prop3_title: string
  prop3_subtext: string
  prop4_title: string
  prop4_subtext: string

  // Navigation & Footer (JSON arrays)
  categories_json: string
  footer_care_links_json: string
  footer_collections_title: string
  footer_care_title: string
  footer_brand_blurb: string

  // Company master (GST & legal)
  company_legal_name: string
  gst_enabled: string
  gstin_number: string

  // Rates & Contact Details
  metal_rates_json: string
  contact_phone: string
  contact_email: string
  store_address: string
  free_delivery_min_amount: string
  standard_shipping_amount: string
}

const DEFAULT_CONFIG: SiteConfig = {
  brand_name: "Buddy's Choice",
  brand_tagline: 'Gold Plated Fashion Jewellery',
  logo_url: '',
  favicon_url: '',
  announcement_ticker: '✨ Premium Gold Plated Covering Jewellery | 1-Year Color Warranty | COD Across India',
  guarantee_badge_text: 'Premium Gold Plated • 1-Yr Color Warranty • COD Available',

  hero_title_line1: 'Crafted to Perfection.',
  hero_title_line2: 'Gold Plated Elegance.',
  hero_subtext: 'Discover handcrafted gold plated rings, temple harams, CZ bracelets, jhumkas and fashion accessories — luxurious finish at everyday prices.',
  hero_cta_primary: 'Explore Collection',
  hero_cta_secondary: 'Browse Categories',
  hero_stat1_value: '1 Year',
  hero_stat1_label: 'Color Warranty',
  hero_stat2_value: '',
  hero_stat2_label: '',
  hero_stat3_value: 'Insured',
  hero_stat3_label: 'Express COD',
  hero_showcase_badge: "BUDDY'S CHOICE SPECIAL EDITION",
  hero_showcase_title: 'Heritage Temple Haram',
  hero_showcase_price: '₹3,999',
  hero_showcase_image: '',

  category_section_title: 'SHOP BY CATEGORY',
  category_section_subtitle: 'Explore our handcrafted gold plated covering collections.',
  catalogue_section_title: 'CURATED JEWELLERY CATALOGUE',
  catalogue_section_subtitle: 'Premium gold plated fashion jewellery & accessories.',

  vip_badge_text: 'VIP EXCLUSIVE ACCESS',
  vip_title: "Join the Buddy's Choice VIP Circle",
  vip_subtext: 'Subscribe for exclusive new collection launches, secret member discounts, and daily metal rate updates.',
  vip_button_text: 'Subscribe',

  prop1_title: 'Premium Gold Plated',
  prop1_subtext: 'Fashion Jewellery with Rich Gold Finish',
  prop2_title: '1-Year Color Warranty',
  prop2_subtext: 'Free Color Replacement',
  prop3_title: 'Express COD Shipping',
  prop3_subtext: 'Insured Delivery Across India',
  prop4_title: 'GST Tax Invoicing',
  prop4_subtext: '3% CGST + 3% SGST Itemized',

  categories_json: '',
  footer_care_links_json: '',
  footer_collections_title: 'Curated Collections',
  footer_care_title: 'Customer Care & Help',
  footer_brand_blurb: '',

  company_legal_name: "Buddy's Choice Jewellery Pvt. Ltd.",
  gst_enabled: 'false',
  gstin_number: '',

  metal_rates_json: '{"gold24k":"7450","gold22k":"6830","silver925":"94"}',
  contact_phone: '+91 98765 43210',
  contact_email: 'support@buddyschoice.com',
  store_address: 'Annur, Coimbatore, Tamil Nadu',
  free_delivery_min_amount: '499',
  standard_shipping_amount: '49',
}

interface SiteConfigContextType {
  config: SiteConfig
  loading: boolean
  updateConfig: (newConfig: Partial<SiteConfig>) => Promise<boolean>
}

const SiteConfigContext = createContext<SiteConfigContextType>({
  config: DEFAULT_CONFIG,
  loading: false,
  updateConfig: async () => false,
})

export const SiteConfigProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [config, setConfig] = useState<SiteConfig>(DEFAULT_CONFIG)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchSettings()
  }, [])

  useEffect(() => {
    // Dynamic document title update
    if (config.brand_name) {
      document.title = `${config.brand_name} — ${config.brand_tagline}`
    }

    // Dynamic favicon update
    if (config.favicon_url) {
      let link: HTMLLinkElement | null = document.querySelector("link[rel*='icon']")
      if (!link) {
        link = document.createElement('link')
        link.type = 'image/x-icon'
        link.rel = 'shortcut icon'
        document.getElementsByTagName('head')[0]?.appendChild(link)
      }
      link.href = config.favicon_url
    }
  }, [config])

  const fetchSettings = async () => {
    try {
      setLoading(true)
      const data = await apiGet<Record<string, string>>('settings')
      if (data && typeof data === 'object' && Object.keys(data).length > 0) {
        setConfig((prev) => ({
          ...prev,
          ...data,
        }))
      }
    } catch (err) {
      console.warn('Using default site configuration:', err)
    } finally {
      setLoading(false)
    }
  }

  const updateConfig = async (newConfig: Partial<SiteConfig>): Promise<boolean> => {
    try {
      const updated = await apiPut<Record<string, string>>('settings', newConfig)
      if (updated && typeof updated === 'object') {
        setConfig((prev) => ({
          ...prev,
          ...updated,
        }))
        return true
      }
      return false
    } catch (err) {
      console.error('Failed to save settings:', err)
      return false
    }
  }

  return (
    <SiteConfigContext.Provider value={{ config, loading, updateConfig }}>
      {children}
    </SiteConfigContext.Provider>
  )
}

export const useSiteConfig = () => useContext(SiteConfigContext)
